// Per-assignment coverage diagnosis for the producer Analytics view.
//
// Replaces the old "did any presence_log overlap the shift window?"
// boolean with a 4-way category + a covered-minutes number that's
// bounded at the shift's expected duration.
//
// The categories distinguish *why* an assignment is undetected:
//   - detected           — crew was at the right stage during the shift
//   - not_tracked        — zero GPS pings during the shift window
//                          (bundle bug, phone off, permission denied)
//   - elsewhere          — pings exist during shift but all on other
//                          polygons (crew was at a different stage)
//   - data_inconsistent  — pings on the right polygon but no presence_log
//                          (RPC missed the ENTER; treat as detected for
//                          coverage purposes because the GPS evidence
//                          is unambiguous)
//
// Without this distinction, a "0% coverage" producer dashboard lumps
// together "crew didn't show up" and "our infrastructure failed to
// track them" — and "we just fixed the infrastructure" makes the
// latter dominate every historical festival snapshot.

export type AssignmentCategory = "detected" | "not_tracked" | "elsewhere" | "data_inconsistent";

export interface PingInput {
  user_id: string;
  timestamp: string;
  current_stage_zone_id: string | null;
}

export interface PresenceLogInput {
  user_id: string;
  entered_at: string;
  left_at: string | null;
  /** festival_area.briefing_stage_id — the resolved stage FK. */
  briefing_stage_id: string | null;
  /** festival_area.id — the polygon UUID. */
  stage_zone_id: string;
}

export interface AssignmentAnalysisInput {
  user_id: string | null;
  stage_id: string | null;
  startUtcMs: number;
  endUtcMs: number;
  expectedMinutes: number;
}

export interface AssignmentAnalysis {
  category: AssignmentCategory;
  coveredMinutes: number;
  pingsInShift: number;
  pingsOnAssignedPolygon: number;
}

/**
 * Analyze a single assignment against the user's GPS pings + presence_logs.
 *
 * `polygonIdsByStageId` maps each briefing_stage.id to the set of
 * festival_areas.id values that resolve to it (one stage can have
 * multiple polygons across festival days).
 */
export function analyzeAssignment(
  assignment: AssignmentAnalysisInput,
  args: {
    pings: PingInput[];
    presenceLogs: PresenceLogInput[];
    polygonIdsByStageId: Map<string, Set<string>>;
  },
): AssignmentAnalysis {
  // Assignments without a user_id (crew row not yet linked to an auth
  // user) or without a stage_id (Other/free-text assignment) can't be
  // tracked — treat as not_tracked so they don't drag the coverage
  // score down for crew whose apps WERE working.
  if (!assignment.user_id || !assignment.stage_id) {
    return {
      category: "not_tracked",
      coveredMinutes: 0,
      pingsInShift: 0,
      pingsOnAssignedPolygon: 0,
    };
  }

  const assignedPolygons = args.polygonIdsByStageId.get(assignment.stage_id) ?? new Set<string>();

  // Pings during the shift window, by this user.
  const pingsInShift = args.pings.filter((p) => {
    if (p.user_id !== assignment.user_id) return false;
    const t = new Date(p.timestamp).getTime();
    return t >= assignment.startUtcMs && t <= assignment.endUtcMs;
  });

  if (pingsInShift.length === 0) {
    return {
      category: "not_tracked",
      coveredMinutes: 0,
      pingsInShift: 0,
      pingsOnAssignedPolygon: 0,
    };
  }

  const pingsOnAssignedPolygon = pingsInShift.filter(
    (p) => p.current_stage_zone_id != null && assignedPolygons.has(p.current_stage_zone_id),
  );

  // presence_log overlap, clipped to the shift window. Open logs cap
  // at the user's last GPS ping ON THIS POLYGON (no magic number —
  // derived from actual data). If there are no polygon-specific pings,
  // an open log's leftAt falls back to its own entered_at, contributing
  // zero overlap.
  let polygonPingMaxMs = 0;
  for (const p of args.pings) {
    if (p.user_id !== assignment.user_id) continue;
    if (p.current_stage_zone_id == null) continue;
    if (!assignedPolygons.has(p.current_stage_zone_id)) continue;
    const t = new Date(p.timestamp).getTime();
    if (t > polygonPingMaxMs) polygonPingMaxMs = t;
  }

  let overlapMs = 0;
  for (const log of args.presenceLogs) {
    if (log.user_id !== assignment.user_id) continue;
    // Match by FK first (post-#127), label match handled upstream.
    if (log.briefing_stage_id !== assignment.stage_id) continue;
    const enteredMs = new Date(log.entered_at).getTime();
    const leftMs = log.left_at
      ? new Date(log.left_at).getTime()
      : polygonPingMaxMs > enteredMs
        ? polygonPingMaxMs
        : enteredMs;
    const overlapStart = Math.max(enteredMs, assignment.startUtcMs);
    const overlapEnd = Math.min(leftMs, assignment.endUtcMs);
    if (overlapEnd > overlapStart) overlapMs += overlapEnd - overlapStart;
  }

  // Clip per assignment so coverage % can never exceed 100% per stage.
  const coveredMinutes = Math.min(assignment.expectedMinutes, overlapMs / 60000);

  // Categorisation: prefer the strongest evidence available.
  // - data_inconsistent: pings on the right polygon, but the RPC never
  //   wrote a presence_log entry (known limitation of the retro
  //   backfill — it re-stamps crew_locations but not stage_presence_logs).
  //   We treat these as detected because the GPS evidence is direct.
  // - detected: clear presence_log overlap with the shift
  // - elsewhere: pings exist during shift but none on the assigned polygon
  if (overlapMs > 0) {
    return {
      category: "detected",
      coveredMinutes,
      pingsInShift: pingsInShift.length,
      pingsOnAssignedPolygon: pingsOnAssignedPolygon.length,
    };
  }
  if (pingsOnAssignedPolygon.length > 0) {
    return {
      category: "data_inconsistent",
      // No presence_log → use ping density as a proxy. Estimate covered
      // minutes from ping count × typical interval, capped at expected.
      // Transistor's default in the active codebase emits a ping
      // roughly every minute in continuous mode; one ping ≈ one minute
      // of presence. This is conservative — under-estimates rather
      // than over-estimates a coverage we can't prove from logs alone.
      coveredMinutes: Math.min(assignment.expectedMinutes, pingsOnAssignedPolygon.length),
      pingsInShift: pingsInShift.length,
      pingsOnAssignedPolygon: pingsOnAssignedPolygon.length,
    };
  }
  return {
    category: "elsewhere",
    coveredMinutes: 0,
    pingsInShift: pingsInShift.length,
    pingsOnAssignedPolygon: 0,
  };
}
